/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import ProTable from '@ant-design/pro-table';
import { Descriptions, Modal, Select, Spin, Typography, message } from 'antd';

/* 单条消息的渲染：会话记录页（/rpaMessageCollection/queryMessageCollectionList）
   与本组件「查看证据」弹窗共用同一份，别在这里另写一套。 */
import ChatMessageItem from '@/components/ChatMessageItem';
import { EmptyState, InfoGrid } from '../../../ui';
import {
  getCoachServiceSummary,
  getStoreServiceSummary,
  getTenantServicePeriods,
} from '@/services/athena';
import { queryMessageAroundMessageId } from '@/services/userManager';
import styles from './index.less';

const { Text } = Typography;

/**
 * 商家洞察 · 二期（商家服务情况汇总）
 * ==================================================================
 * 承担三步读的 ① ② ③ 三步：
 *
 *   ① POST /athena/tenantServicePeriods → 洞察期下拉的数据源
 *   ② POST /athena/storeServiceSummary  → 选中期的结构化计数 + 洞察明细
 *   ③ POST /athena/coachServiceSummary  → 「教练明细」**普通表格**（ProTable，
 *      与 /athena/queryCoach 同一套写法：分页字段在 page 信封上、回包 data = 裸数组、
 *      page.count = 总数；取数与翻页都交给控件，本组件不维护行数据/页码等中间态）
 *
 * ⚠ 文案口径：后端契约里叫「观察期」（`listAvailablePeriods` / `MAX_PERIOD_LIMIT` /
 *   各模型 javadoc），**端上一律显示「洞察期」**。同一个东西，改动只在展示层。
 *
 * 接口契约（litta-merchant `TenantServiceSummaryResponse`）：
 *   - 🔴 **`periodStatus` 是这块最重要的一条**：`1` = 本期已产出、`0` = 没产出
 *     （job 没跑 / 跑失败 / 该店不在覆盖范围 / 该周还没到）。`0` 时 `store` 为 null、
 *     `insights` 为空 ⇒ 端上必须显示「本期暂无数据」而**不是**「无异常」。
 *   - 时间字段全是 `yyyy-MM-dd` 字符串；`periodStart ~ periodEnd` 是**闭区间**
 *     （库里 `period_end` 存「周日 00:00:00」但数据覆盖周日全天，别写成「截止 periodEnd」）。
 *   - `store` 的四档响应计数**不可合并**：`replyPendingNum` 是「观察窗未满、无法判定」，
 *     并进分母（或并进「超时未响应」）都会算出一个错的响应率。
 *   - `insights` ≤ 2 条，**已按 priority 升序排好，端上不要再排**。
 *   - 及时/长时响应阈值由 Apollo 配置且**不在响应里**，故前端不写死「≤24h」这类数字。
 *
 * ③ 教练明细（litta-merchant `TenantServiceCoachSummaryModel`）：
 *   - 每行**自带** `periodStart / periodEnd`（同一页内必然相同），裸 `List` 也能表达「这是哪一期」；
 *     ⚠ 本接口**没有** `periodStatus`，「有没有数据」只能看 `page.count`。
 *   - ⚠ 行是**全量建行**的：本期服务团队里每个人都会有一行，空行 ≠ 没服务 ⇒ 不要过滤「数字全 0」的行，
 *     否则「这个人这周什么都没干」会直接消失（真要判读得看 `noInputPairNum` / `emptyEventPairNum`）。
 *   - ⚠ `inputState` 教练层只有 **3 档**（`3 = 输入不达标` 是店铺层独有），**别复用 ② 那张 4 档表**。
 *   - `evidences` **不在列表里铺开**（每行最多 100 条、`size()` 不等于真实证据数，
 *     且只有消息指针、没有原文）：改由「操作 · 查看」进弹窗，且只列**带 `reason`（缺口归因）**
 *     的条目。选中一条后拿它的 `messageId` 调
 *     `/rpaMessageCollection/queryWeChatMessageAroundMessageId`（会话记录页那一族的接口）
 *     拉**锚点前 10 + 锚点自身 + 后 10**（≈21 条，按时间正序）当上下文，**不分页**。
 *
 * 证据上下文消息的展示（第 ④ 步）：
 *   - 行渲染直接复用 `@/components/ChatMessageItem` —— 它就是会话记录页的单条渲染，
 *     两处只有一份实现，样式不会漂。
 *   - ⚠ `evidence.messageId` **空串是合法值**（纯跨消息推断的证据只有会话指针）。
 *     这种条目**不发请求**，直接提示「该证据没有关联消息」——
 *     发出去只会拿到一个被 metaspace 侧 `@NotEmpty` 挡回来的失败。
 *
 * @param tenantId 商家ID（必传；为空时不发请求）
 */

/** 输入状态 4 档（服务端派生，端上按档渲染，不要自己算） */
const INPUT_STATE_TEXT = {
  0: '正常',
  1: '无输入（该店群未进输入表）',
  2: '确无事件（有行但事件为空）',
  3: '输入不达标',
};

/** 维度枚举 → 中文。洞察行的 `category` 已是中文，但 `unjudged[]` 只给枚举，得自己映射 */
const DIMENSION_TEXT = {
  response_timeliness: '响应及时性',
  proactive_action: '服务主动性',
  reply_quality: '回复专业度',
  input_quality: '数据可用性',
};

/** 事实指纹尾段 → 中文（`factKey` 四种形态，取最后一段即「是什么」） */
const FACT_TAIL_TEXT = {
  replyOverdue: '超时未响应',
  replySample: '待核消息样本',
  proactiveAction: '主动服务动作',
};

/** `periodStatus` 的「已产出」取值 */
const PERIOD_PRODUCED = 1;

/**
 * 教练层输入状态 **3 档**（⚠ 与店铺层的 4 档不是同一张表：
 * `3 = 输入不达标` 只有店铺层会出，教练层拿不到）。
 */
const COACH_INPUT_STATE_TEXT = {
  0: '正常',
  1: '无输入',
  2: '确无事件',
};

/**
 * 证据的 `reason`（缺口归因）→ 中文。**只有两个取值**，与洞察行 `unjudged[].reason`
 * 那套 8 值闭集合**不是同一套**，别复用那边的渲染。
 * ⚠ 映射表要有兜底：服务端加值时回落原值，不能让整项静默消失。
 */
const REASON_TEXT = {
  overdue: '超时未回复',
  pending: '超窗口未响应',
};

/** ③ 教练明细默认每页条数（服务端 `DEFAULT_PAGE_SIZE` 是 20，端上按 10 更省滚） */
const COACH_PAGE_SIZE = 10;

/**
 * 一条证据事实 → 一行中文。
 * `factKey` 四形态：`store:replyOverdue` / `coach:{id}:replyOverdue` /
 * `coach:{id}:replySample` / `coach:{id}:proactiveAction`
 * ⇒ 有 `coachUserId` 就是某个教练，没有就是整店口径。
 * ⚠ 姓名关联失败时服务端给 null，这里降级显示 `coachUserId` 数字，**不丢这条事实**。
 */
const factText = (f) => {
  const tail = String((f && f.factKey) || '')
    .split(':')
    .pop();
  const who = f.coachUserId ? f.coachName || `教练 ${f.coachUserId}` : '全店';
  const what = FACT_TAIL_TEXT[tail] || tail || '事实';
  return `${who} · ${what}：${f.value ?? '-'}`;
};

/**
 * 一个「没算」的维度 → 一行中文。
 * ⚠ **不自己写 `reason` 的 switch**：服务端已把 8 个 reason 归成两档并给了 `causeText`
 *   （「数据不足」/「模型未给出可信结论」），有值直接显示；为空才按 `causeType` 兜。
 *   后一档不能渲染成「数据不足」——那会把「去查百炼应用 / Apollo」指成「去查库」。
 */
const unjudgedText = (u) => {
  const dim = DIMENSION_TEXT[u.dimension] || u.dimension || '未知维度';
  const cause =
    u.causeText || (u.causeType === 2 ? '模型未给出可信结论' : '数据不足');
  return `${dim}（${cause}）`;
};

/**
 * 一个教练行的左标签：姓名关联失败时降级显示 `coachUserId`，**不丢这一行**
 * （与服务端 `coachName` 可空的约定一致）。
 */
const coachLabel = (c) =>
  (c && c.coachName) || `教练 ${(c && c.coachUserId) ?? '-'}`;

/**
 * 空值占位：表格里 **0 与 null 必须能分开看**
 * —— `0` 是「算了、结果是 0」，`null` 是「这个字段没给」，糊成一样会把「没算」读成「没问题」。
 */
const numCell = (value) =>
  value === null || value === undefined ? '-' : value;

/**
 * 证据行 → 弹窗下拉项。
 * ⚠ `type` 是**开放集合、AI 原始中文取值**（服务端不翻译，翻一张中文表本身就是新的漂移源）
 *   ⇒ 原样显示，**不要**写成 switch，否则新增取值会被静默吞掉；
 * ⚠ `reason` 只有 `overdue` / `pending` 两值，可以映射，但兜底仍回落原值；
 * ⚠ value 用 `refId|messageId` 而不是 `messageId`：后者**空串是合法值**
 *   （纯跨消息推断没有消息级指针），不能当唯一键。
 */
const evidenceOptions = (rows) =>
  rows.map((e) => {
    const reason = REASON_TEXT[e.reason] || e.reason;
    return {
      value: `${e.refId}|${e.messageId}`,
      label: e.type ? `${e.type} · ${reason}` : reason,
    };
  });

/**
 * 教练明细的列。表格控件与 `/athena/queryCoach` 用的是同一个（`ProTable`）。
 * <p>
 * ⚠ 四档响应拆成四列、**不合并**：`待观察` 是「观察窗未满、无法判定」，
 *   并进分母或并进「超时未响应」都会算出一个错的响应率。
 * ⚠ `无输入对` / `事件为空对` 两列**必须留着**：它们是「无输入」与「确无事件」的落地依据，
 *   缺了这两列，全是 0 的行会被读成「这个教练这周什么都没干」。
 * ⚠ 数据列里**没有** `evidences`（对象数组，铺成列没有意义）：证据走末尾的「操作」列进弹窗，
 *   理由同文件头。
 */
const COACH_COLUMNS = [
  {
    title: '教练',
    dataIndex: 'coachName',
    width: 140,
    fixed: 'left',
    render: (_, record) => coachLabel(record),
  },
  {
    title: '有服务事实会员',
    dataIndex: 'servedMemberNum',
    width: 130,
    align: 'right',
    render: numCell,
  },
  {
    title: '绑定会员',
    dataIndex: 'bindMemberNum',
    width: 100,
    align: 'right',
    render: numCell,
  },
  {
    title: '及时响应',
    dataIndex: 'replyTimelyNum',
    width: 100,
    align: 'right',
    render: numCell,
  },
  {
    title: '延迟响应',
    dataIndex: 'replyDelayedNum',
    width: 100,
    align: 'right',
    render: numCell,
  },
  {
    title: '超时未响应',
    dataIndex: 'replyOverdueNum',
    width: 110,
    align: 'right',
    render: numCell,
  },
  {
    title: '待观察',
    dataIndex: 'replyPendingNum',
    width: 100,
    align: 'right',
    render: numCell,
  },
  {
    title: '诉求事件',
    dataIndex: 'demandNum',
    width: 100,
    align: 'right',
    render: numCell,
  },
  {
    title: '输入状态',
    dataIndex: 'inputState',
    width: 110,
    render: (value) => COACH_INPUT_STATE_TEXT[value] || '-',
  },
  {
    title: '无输入对',
    dataIndex: 'noInputPairNum',
    width: 100,
    align: 'right',
    render: numCell,
  },
  {
    title: '事件为空对',
    dataIndex: 'emptyEventPairNum',
    width: 110,
    align: 'right',
    render: numCell,
  },
];

const TenantInsight = (props) => {
  const { tenantId } = props;

  /** 可选洞察期（原样来自接口，已倒序） */
  const [periods, setPeriods] = useState([]);
  /** 当前选中的洞察期起始日 yyyy-MM-dd；只在列表加载成功后落值 */
  const [periodStart, setPeriodStart] = useState(undefined);
  const [periodsLoading, setPeriodsLoading] = useState(false);

  /** ② 的响应 data；null = 未取到 */
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  /** ② 失败：与「本期无产出」是两回事，必须分开呈现，不能都落到「暂无数据」 */
  const [summaryFailed, setSummaryFailed] = useState(false);

  /**
   * ③ 是否失败。**只用来换空表文案**，不参与渲染分支：
   * 分页、加载态、行数据全部交给 ProTable 自己管（与 /athena/queryCoach 同一套写法），
   * 本组件不再持有 `coachRows / coachTotal / coachPage` 这些中间态。
   */
  const [coachFailed, setCoachFailed] = useState(false);

  /**
   * 证据弹窗。`null` = 关闭，收起时**整个对象置 null**——
   * 只置一个 `visible=false` 的话，行数据会留在 state 里，下次打开前会先闪出上一行的证据。
   */
  const [evidence, setEvidence] = useState(null);
  /** 弹窗内下拉的选中项（`refId|messageId`）；每次打开重置，不记住上一次的选择 */
  const [evidenceKey, setEvidenceKey] = useState(undefined);
  /** 选中证据的上下文消息（裸数组）；null = 还没选 / 还没取到 */
  const [evidenceMessages, setEvidenceMessages] = useState(null);
  const [evidenceMessagesLoading, setEvidenceMessagesLoading] = useState(false);
  /** 取数失败：与「该消息附近确实没有内容」是两回事，不能都落到「暂无数据」 */
  const [evidenceMessagesFailed, setEvidenceMessagesFailed] = useState(false);

  /* ---------- ① 洞察期列表 ---------- */
  useEffect(() => {
    if (!tenantId) return undefined;

    /* 与 powerSummary 同一套生命周期写法：cancelled 挡掉「切了商家之后
       旧请求才回来」——否则旧数据会覆盖新商家的，选中项直接错位。 */
    let cancelled = false;
    setPeriodsLoading(true);
    /* 换商家先清空：旧期次 / 旧汇总留在界面上，会被读成新商家的数据 */
    setPeriods([]);
    setPeriodStart(undefined);
    setSummary(null);
    setSummaryFailed(false);
    /* 旧商家的教练行更危险：带着上一家的名字与计数。表格数据由 ProTable 自己管，
       这里只需把「上一家失败过」的标记清掉，新请求会重新落这一次的结果。 */
    setCoachFailed(false);

    getTenantServicePeriods({ requestData: { tenantId } })
      .then((res) => {
        if (cancelled) return;
        setPeriodsLoading(false);
        if (!res || res.code !== '00000') {
          message.error((res && res.msg) || '洞察期列表加载失败');
          return;
        }
        const list = res.data || [];
        setPeriods(list);
        /* 倒序第一条 = 最新一期，直接选中：省掉「首屏没有期次 ⇒ ②③ 发不出去」 */
        setPeriodStart(list.length ? list[0].periodStart : undefined);
      })
      .catch(() => {
        if (cancelled) return;
        setPeriodsLoading(false);
        message.error('洞察期列表加载失败，请稍后重试');
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /* ---------- ② 选中期的店铺服务汇总 ---------- */
  useEffect(() => {
    /* periodStart 为空（下拉还没数据）时没有可查的期次，不发请求 */
    if (!tenantId || !periodStart) return undefined;

    let cancelled = false;
    setSummaryLoading(true);
    setSummaryFailed(false);

    getStoreServiceSummary({ requestData: { tenantId, periodStart } })
      .then((res) => {
        if (cancelled) return;
        setSummaryLoading(false);
        /* data 为 null 也按失败处理（网关层异常会这样），不落到「本期无产出」 */
        if (!res || res.code !== '00000' || !res.data) {
          setSummary(null);
          setSummaryFailed(true);
          message.error((res && res.msg) || '店铺服务汇总加载失败');
          return;
        }
        setSummary(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setSummaryLoading(false);
        setSummary(null);
        setSummaryFailed(true);
        message.error('店铺服务汇总加载失败，请稍后重试');
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, periodStart]);

  /* ---------- ③ 的取数不在这里 ----------
     教练明细是「普通表格」，取数/分页/加载态全交给 ProTable 的 `request` + `params`
     （与 /athena/queryCoach 同一套写法）。换期次时 `params` 变化，
     ProTable 自己会把页码归 1 并重新请求 —— 所以本组件不再维护 coach 的
     行数据 / 总数 / 页码 / loading 这几个中间态，也就没有对应的 effect。 */

  /* ---------- ④ 证据弹窗：选中一条证据后拉它的上下文消息 ----------
     依赖 `evidence`（换教练会整体换对象）+ `evidenceKey`（换选中的证据）。
     ⚠ 选中的证据没有 `messageId`（空串是合法值：跨消息推断只有会话指针）时不发请求，
       否则只会拿到一个被 metaspace 侧 `@NotEmpty` 挡回来的失败。 */
  useEffect(() => {
    const picked =
      evidence && evidenceKey
        ? evidence.rows.find((e) => `${e.refId}|${e.messageId}` === evidenceKey)
        : null;
    const messageId = picked ? picked.messageId : '';

    /* 换证据先清空：旧消息留在下面会被读成新证据的上下文 */
    setEvidenceMessages(null);
    setEvidenceMessagesFailed(false);
    if (!messageId) {
      setEvidenceMessagesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setEvidenceMessagesLoading(true);

    queryMessageAroundMessageId({ requestData: { messageId } })
      .then((res) => {
        if (cancelled) return;
        setEvidenceMessagesLoading(false);
        if (!res || res.code !== '00000') {
          setEvidenceMessages([]);
          setEvidenceMessagesFailed(true);
          message.error((res && res.msg) || '证据上下文消息加载失败');
          return;
        }
        /* data = 裸数组（已按时间正序），本接口不分页，整段直接渲染 */
        setEvidenceMessages(res.data || []);
      })
      .catch(() => {
        if (cancelled) return;
        setEvidenceMessagesLoading(false);
        setEvidenceMessages([]);
        setEvidenceMessagesFailed(true);
        message.error('证据上下文消息加载失败，请稍后重试');
      });

    return () => {
      cancelled = true;
    };
  }, [evidence, evidenceKey]);

  /* periodStart 在接口侧已 distinct，可直接当 value；
     展示成闭区间 `start ~ end`，与接口契约一致。 */
  const options = periods.map((p) => ({
    value: p.periodStart,
    label: p.periodEnd ? `${p.periodStart} ~ ${p.periodEnd}` : p.periodStart,
  }));

  const noPeriod = !periodsLoading && !periods.length;

  /**
   * 点「查看证据」：
   *   - `evidences` 为空（服务端约定无证据是**空列表**、不是 null）⇒ 只提示，不开弹窗；
   *   - 有数据但一条 `reason` 都没有 ⇒ 同样只提示：`reason` 是缺口归因，
   *     没有它的是**背景证据**，装进下拉会得到一个空下拉框；
   *   - 否则只把带 `reason` 的装进下拉。
   */
  const openEvidence = (record) => {
    const raw = (record && record.evidences) || [];
    if (!raw.length) {
      message.info('该教练本期暂无证据');
      return;
    }
    const rows = raw.filter((e) => e.reason);
    if (!rows.length) {
      message.info('该教练本期的证据均无缺口归因，暂无可查看项');
      return;
    }
    setEvidenceKey(undefined);
    setEvidence({ coach: coachLabel(record), rows });
  };

  /* 「操作」列在组件内拼：render 里要调上面这个开弹窗的回调，
     数据列仍用模块级常量（它们与组件状态无关）。 */
  const coachColumns = [
    ...COACH_COLUMNS,
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Typography.Link onClick={() => openEvidence(record)}>
          查看
        </Typography.Link>
      ),
    },
  ];

  /** ② 的结果区：失败 / 本期无产出 / 结构化 + 明细 */
  const renderSummaryBody = () => {
    if (summaryFailed) {
      return (
        <EmptyState
          description="店铺服务汇总加载失败"
          hint="可切换其他洞察期，或稍后重试"
        />
      );
    }
    if (!summary) return null;

    /* 🔴 「没产出」≠「没问题」：这块文案就是防这个误读的 */
    if (summary.periodStatus !== PERIOD_PRODUCED) {
      return (
        <EmptyState
          description="本期暂无数据"
          hint="该期数据未产出（job 未跑 / 跑失败 / 该店不在覆盖范围），不等于服务无异常"
        />
      );
    }

    const store = summary.store || {};
    const insights = summary.insights || [];

    return (
      <>
        <div className={styles.period}>
          <Text type="secondary">
            {`洞察期 ${summary.periodStart || '-'} ~ ${
              summary.periodEnd || '-'
            }`}
            {summary.dataGeneratedAt
              ? ` · 数据截至 ${summary.dataGeneratedAt}`
              : ''}
          </Text>
        </div>

        {/* ---- 结构化数据 ----
            不展示 `store.coachNum`（服务团队人数）：页面别处已有这个数，
            这里重复会把「服务情况」的重点冲淡。剩下 8 项正好 4×2 铺满。 */}
        <InfoGrid
          className={styles.block}
          column={4}
          data={[
            {
              key: 'servedCoachNum',
              label: '本期有服务事实教练数',
              value: store.servedCoachNum,
            },
            {
              key: 'memberNumDedup',
              label: '会员数 · 跨教练去重',
              value: store.memberNumDedup,
            },
            {
              key: 'memberNumSummed',
              label: '会员数 · 各教练合计',
              value: store.memberNumSummed,
            },
            {
              key: 'replyTimelyNum',
              label: '及时响应',
              value: store.replyTimelyNum,
            },
            {
              key: 'replyDelayedNum',
              label: '延迟响应',
              value: store.replyDelayedNum,
            },
            {
              key: 'replyOverdueNum',
              label: '超时未响应',
              value: store.replyOverdueNum,
            },
            {
              key: 'replyPendingNum',
              label: '待观察 · 窗口未满',
              value: store.replyPendingNum,
            },
            {
              key: 'inputState',
              label: '输入状态',
              value: INPUT_STATE_TEXT[store.inputState] || '-',
            },
          ]}
        />

        {/* ---- 洞察明细：左维度 / 右明细，与 POWER总结 同一形态 ---- */}
        <Descriptions
          className={styles.block}
          title="洞察明细"
          bordered
          column={1}
          labelStyle={{ width: 140 }}
          contentStyle={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
        >
          {insights.length ? (
            insights.map((ins) => (
              <Descriptions.Item
                /* 业务键 (tenant_id, insight_key, period_start) ⇒ 一期内一个维度只有一条 */
                key={ins.insightKey}
                label={ins.category || '-'}
              >
                <div>{ins.topic || '-'}</div>
                {ins.conclusion ? <div>结论：{ins.conclusion}</div> : null}
                {ins.action ? (
                  <div>
                    建议动作：{ins.action}
                    {ins.actionRole ? `（${ins.actionRole}）` : ''}
                  </div>
                ) : null}
                {(ins.facts || []).length ? (
                  <div>证据：{ins.facts.map(factText).join('；')}</div>
                ) : null}
                {(ins.unjudged || []).length ? (
                  <div>未评估：{ins.unjudged.map(unjudgedText).join('；')}</div>
                ) : null}
              </Descriptions.Item>
            ))
          ) : (
            /* insights 为空且 periodStatus=1 ⇒ 既没被点名、也没有缺口行 = 判过了且没问题 */
            <Descriptions.Item label="结论">
              本期已判过，未发现需要提示的服务缺口
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* ---- 教练明细：普通表格 ----
             控件与 /athena/queryCoach 一致（ProTable + request + params）：
             换期次 / 换商家只改 `params`，控件自己把页码归 1 并重新请求，
             所以这里不写页码 state、也不写「翻页要重查」的 effect。 */}
        <div className={styles.block}>
          <ProTable
            rowKey="coachUserId"
            headerTitle="教练明细"
            columns={coachColumns}
            /* 纯下钻列表：不要查询表单、不要工具栏的刷新/密度/列设置 */
            search={false}
            options={false}
            /* 不要再套一层白卡片（外层 SectionCard 已经是卡片） */
            cardProps={false}
            params={{ tenantId, periodStart }}
            scroll={{ x: 'max-content' }}
            locale={{
              /* ③ 没有 periodStatus，「空」只能说到这：该期可能跑过但这店没有教练行，
                 也可能压根没产出。⚠ 与「加载失败」必须分开，不能都落到「暂无数据」。 */
              emptyText: coachFailed ? (
                <EmptyState
                  description="教练明细加载失败"
                  hint="可切换其他洞察期，或稍后重试"
                  compact
                />
              ) : (
                <EmptyState description="本期没有可展示的教练行" compact />
              ),
            }}
            pagination={{
              size: 'small',
              defaultPageSize: COACH_PAGE_SIZE,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `共 ${total} 个教练`,
            }}
            request={async (reqParams) => {
              /* ⚠ 分页字段在 page 信封上，不在 requestData 里（与 queryCoach 同构） */
              let res;
              try {
                res = await getCoachServiceSummary({
                  requestData: { tenantId, periodStart },
                  page: {
                    currentPage: reqParams.current,
                    pageSize: reqParams.pageSize,
                  },
                });
              } catch (e) {
                setCoachFailed(true);
                message.error('教练明细加载失败，请稍后重试');
                return { success: false };
              }
              if (!res || res.code !== '00000') {
                setCoachFailed(true);
                message.error((res && res.msg) || '教练明细加载失败');
                return { success: false };
              }
              setCoachFailed(false);
              /* data = 裸数组（每行自带 periodStart / periodEnd）；总数只在 page.count 上 */
              return {
                data: res.data || [],
                success: true,
                total: (res.page && res.page.count) || 0,
              };
            }}
          />
        </div>
      </>
    );
  };

  /**
   * 证据弹窗的下半部分：选中证据的上下文消息（**不分页**，整段铺出来）。
   * ⚠ 四种状态必须分开，绝不能都落到「暂无数据」：
   *   未选 / 选中的证据没有 `messageId` / 加载失败 / 已取到（可能是空数组）。
   */
  const renderEvidenceMessages = () => {
    const picked =
      evidence && evidenceKey
        ? evidence.rows.find((e) => `${e.refId}|${e.messageId}` === evidenceKey)
        : null;

    if (!evidenceKey) {
      return <EmptyState description="选择证据后展示上下文消息" compact />;
    }
    if (!picked) return null;
    /* 空串是合法值：跨消息推断出来的证据只有会话指针，没有单条消息可回查 */
    if (!picked.messageId) {
      return (
        <EmptyState
          description="该证据没有关联消息"
          hint="跨消息推断出来的证据只有会话指针，没有可回查的单条消息"
          compact
        />
      );
    }
    if (evidenceMessagesFailed) {
      return (
        <EmptyState
          description="上下文消息加载失败"
          hint="可重新选择一条证据，或稍后重试"
          compact
        />
      );
    }
    if (evidenceMessagesLoading) {
      return (
        <div className={styles.evidenceLoading}>
          <Spin />
        </div>
      );
    }

    const list = evidenceMessages || [];
    if (!list.length) {
      return <EmptyState description="该消息附近没有可展示的内容" compact />;
    }
    return (
      <div className={styles.evidenceList}>
        {/* 行渲染与会话记录页完全同一个组件：payload 已由服务端解析成对象 */}
        {list.map((m, i) => (
          <ChatMessageItem key={m.id || m.messageId || i} record={m} />
        ))}
      </div>
    );
  };

  return (
    <>
      <Spin spinning={periodsLoading || summaryLoading}>
        <div className={styles.row}>
          <span>洞察期</span>
          <Select
            value={periodStart}
            /* 只清「上一期失败过」的标记：③ 表的页码归位由 ProTable 自己按 params 变化处理 */
            onChange={(value) => {
              setCoachFailed(false);
              setPeriodStart(value);
            }}
            options={options}
            disabled={noPeriod}
            placeholder={noPeriod ? '暂无洞察期数据' : '请选择洞察期'}
            notFoundContent={periodsLoading ? '加载中…' : '暂无洞察期数据'}
            style={{ width: 260 }}
          />
        </div>
        {periodsLoading ? null : renderSummaryBody()}
      </Spin>

      {/* 证据弹窗放在 Spin 外：它是覆盖层，没必要跟着卡片一起处理加载态 */}
      {evidence ? (
        <Modal
          title={`查看证据 · ${evidence.coach}`}
          /* 项目是 antd 4.x：受控属性是 visible（open 是 v5 的改名），与 deductPlanModal 一致 */
          visible
          /* 下半部分是一个消息流（头像 + 昵称 + 内容），520 会把每条压成两行，放宽一档 */
          width={720}
          /* 只做查看，没有提交动作 ⇒ 不要确定/取消按钮 */
          footer={null}
          onCancel={() => setEvidence(null)}
        >
          <Select
            value={evidenceKey}
            onChange={setEvidenceKey}
            options={evidenceOptions(evidence.rows)}
            placeholder="请选择证据"
            style={{ width: '100%' }}
          />

          {/* ---- 上下文消息：锚点前 10 + 自身 + 后 10，不分页 ---- */}
          <div className={styles.evidenceMessages}>
            {renderEvidenceMessages()}
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default TenantInsight;
